

import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import io from 'socket.io-client';
import { useAuth } from '../context/AuthContext';
import { useAxios } from './useAxios';

const API_BASE_URL = process.env.REACT_APP_BASE_URL || 'https://api.rismicmart.com';

export const useNotifications = () => {
  const { user } = useAuth();
  const axiosInstance = useAxios();
  const navigate = useNavigate();

  const [notificationCount, setNotificationCount] = useState(0);
  const [showNotificationDropdown, setShowNotificationDropdown] = useState(false);
  const [pendingNotifications, setPendingNotifications] = useState([]);
  const [cancelledNotifications, setCancelledNotifications] = useState([]); // ✅ ADDED
  const [activeOrders, setActiveOrders] = useState([]);
  const [socketConnected, setSocketConnected] = useState(false);
  const socketRef = useRef(null);
  const audioRef = useRef(null);

  // ==================== INITIALIZE NOTIFICATION SOUND ====================
  useEffect(() => {
    audioRef.current = new Audio('/notification-sound.mp3');
    audioRef.current.volume = 0.7;
  }, []);

  // ==================== PLAY NOTIFICATION SOUND ====================
  const playNotificationSound = useCallback(() => {
    if (audioRef.current) {
      audioRef.current.play().catch(err => {
        console.log('Audio play failed:', err);
      });
    }
  }, []);

  // ==================== FETCH ACTIVE ORDERS (CORRECTED) ====================
  const fetchActiveOrders = useCallback(async () => {
    if (!user?._id) {
      console.log('❌ No user ID available');
      return;
    }

    try {
      console.log('📦 Fetching active orders for:', user._id);
      
      const response = await axiosInstance.get(
        `/api/orders/delivery-person/${user._id}`
      );

      if (response.data.success) {
        const orders = response.data.data || [];
        
        // CORRECTED: Include confirmed, shipped, and out_for_delivery
        const active = orders.filter((order) =>
          ['confirmed', 'shipped', 'out_for_delivery'].includes(order.status)
        );
        setActiveOrders(active);

        // Pending notifications include ALL active orders
        setPendingNotifications(active);
        
        // ✅ FILTER CANCELLED ORDERS FROM RECENT ORDERS
        const recentCancelled = orders.filter(order => order.status === 'cancelled');
        setCancelledNotifications(recentCancelled.slice(0, 5)); // Keep only 5 recent cancelled
        
        // ✅ UPDATE TOTAL NOTIFICATION COUNT (pending + cancelled)
        const totalCount = active.length + recentCancelled.length;
        setNotificationCount(totalCount);

        console.log('✅ Active orders fetched:', active.length);
        console.log('🔔 Pending notifications:', active.length);
        console.log('🚫 Cancelled notifications:', recentCancelled.length);
        console.log('📊 Total notifications:', totalCount);
        console.log('📊 Status breakdown:', {
          confirmed: active.filter(o => o.status === 'confirmed').length,
          shipped: active.filter(o => o.status === 'shipped').length,
          out_for_delivery: active.filter(o => o.status === 'out_for_delivery').length,
          cancelled: recentCancelled.length
        });
      }
    } catch (error) {
      console.error('❌ Error fetching active orders:', error);
    }
  }, [user?._id, axiosInstance]);

  // ==================== FETCH ORDERS ON MOUNT ====================
  useEffect(() => {
    if (user?._id) {
      fetchActiveOrders();
    }
  }, [user?._id, fetchActiveOrders]);

  // ==================== REQUEST NOTIFICATION PERMISSION ====================
  useEffect(() => {
    if ('Notification' in window && Notification.permission === 'default') {
      Notification.requestPermission().then((permission) => {
        console.log('Notification permission:', permission);
      });
    }
  }, []);

  // ==================== SOCKET CONNECTION ====================
  useEffect(() => {
    if (!user?._id) {
      console.log('⚠️ No user ID, skipping socket connection');
      return;
    }

    console.log('\n═══════════════════════════════════════');
    console.log('🔌 INITIALIZING NOTIFICATION SOCKET');
    console.log('═══════════════════════════════════════');
    console.log('User ID:', user._id);
    console.log('User Name:', user.fullName);
    console.log('API URL:', API_BASE_URL);
    console.log('Time:', new Date().toLocaleString());
    console.log('═══════════════════════════════════════\n');

    // Create socket connection
    const socket = io(API_BASE_URL, {
      transports: ['websocket', 'polling'],
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionAttempts: 10,
      reconnectionDelayMax: 5000,
      timeout: 20000,
      autoConnect: true,
      forceNew: true
    });

    socketRef.current = socket;

    // ==================== CONNECTION EVENTS ====================

    socket.on('connect', () => {
      console.log('\n✅ ═══════════════════════════════════════');
      console.log('SOCKET CONNECTED SUCCESSFULLY');
      console.log('═══════════════════════════════════════');
      console.log('Socket ID:', socket.id);
      console.log('Transport:', socket.io.engine.transport.name);
      console.log('Time:', new Date().toLocaleString());
      console.log('═══════════════════════════════════════\n');

      setSocketConnected(true);

      // Register delivery person
      console.log('📝 Registering delivery person:', user._id);
      socket.emit('register-delivery-person', user._id);

      // ✅ JOIN DELIVERY PERSON ROOM
      socket.emit('join-room', `delivery-person-${user._id}`);

      toast.success('🔔 Connected to notification system!', {
        position: 'bottom-right',
        autoClose: 2000
      });
    });

    socket.on('connect_error', (error) => {
      console.error('\n❌ ═══════════════════════════════════════');
      console.error('SOCKET CONNECTION ERROR');
      console.error('═══════════════════════════════════════');
      console.error('Error:', error.message);
      console.error('Time:', new Date().toLocaleString());
      console.error('═══════════════════════════════════════\n');
      setSocketConnected(false);
    });

    socket.on('disconnect', (reason) => {
      console.log('\n🔌 ═══════════════════════════════════════');
      console.log('SOCKET DISCONNECTED');
      console.log('═══════════════════════════════════════');
      console.log('Reason:', reason);
      console.log('Time:', new Date().toLocaleString());
      console.log('═══════════════════════════════════════\n');
      setSocketConnected(false);
    });

    socket.on('reconnect', (attemptNumber) => {
      console.log(`\n🔄 ═══════════════════════════════════════`);
      console.log(`RECONNECTED AFTER ${attemptNumber} ATTEMPTS`);
      console.log(`═══════════════════════════════════════\n`);
      setSocketConnected(true);
      socket.emit('register-delivery-person', user._id);
      socket.emit('join-room', `delivery-person-${user._id}`);
    });

    socket.on('reconnect_attempt', (attemptNumber) => {
      console.log(`🔄 Reconnection attempt ${attemptNumber}...`);
    });

    socket.on('reconnect_error', (error) => {
      console.error('❌ Reconnection error:', error.message);
    });

    // ==================== REGISTRATION EVENTS ====================

    socket.on('registration-success', (data) => {
      console.log('\n✅ ═══════════════════════════════════════');
      console.log('REGISTRATION CONFIRMED BY SERVER');
      console.log('═══════════════════════════════════════');
      console.log('Data:', JSON.stringify(data, null, 2));
      console.log('═══════════════════════════════════════\n');
    });

    socket.on('registration-error', (data) => {
      console.error('\n❌ ═══════════════════════════════════════');
      console.error('REGISTRATION ERROR');
      console.error('═══════════════════════════════════════');
      console.error('Error:', data);
      console.error('═══════════════════════════════════════\n');
      
      toast.error('Failed to register for notifications', {
        position: 'bottom-right',
        autoClose: 3000
      });
    });

    socket.on('welcome', (data) => {
      console.log('👋 Welcome message:', data);
    });

    // ==================== ORDER ASSIGNMENT (MAIN EVENT) ====================

    socket.on('order:assigned', (data) => {
      console.log('\n🔔 ═══════════════════════════════════════');
      console.log('NEW ORDER ASSIGNED - EVENT RECEIVED!');
      console.log('═══════════════════════════════════════');
      console.log('Full Data:', JSON.stringify(data, null, 2));
      console.log('Order ID:', data.orderId);
      console.log('Order Amount:', data.order?.amount);
      console.log('Customer:', data.order?.name);
      console.log('Timestamp:', data.timestamp);
      console.log('═══════════════════════════════════════\n');

      // Play sound
      playNotificationSound();

      // Add to active orders
      if (data.order) {
        setActiveOrders((prevOrders) => {
          const exists = prevOrders.some((order) => order._id === data.orderId);
          if (!exists) {
            console.log('➕ Adding new order to list');
            return [data.order, ...prevOrders];
          }
          console.log('⚠️ Order already in list');
          return prevOrders;
        });

        // Add to pending notifications
        setPendingNotifications((prev) => {
          const exists = prev.some((order) => order._id === data.orderId);
          if (!exists) {
            return [data.order, ...prev];
          }
          return prev;
        });

        // Update count
        setNotificationCount((prev) => prev + 1);
      }

      // Browser notification
      if ('Notification' in window && Notification.permission === 'granted') {
        new Notification('New Delivery Order! 🚚', {
          body: `Order #${data.orderId?.slice(-6)} - ₹${data.order?.amount || 0}`,
          icon: '/logo192.png',
          requireInteraction: true
        });
      }

      // Toast notification
      toast.info(`🔔 New Order! #${data.orderId?.slice(-6)}`, {
        position: 'top-right',
        autoClose: 5000
      });

      // Refresh orders
      setTimeout(() => {
        fetchActiveOrders();
      }, 1000);
    });

    // ==================== LEGACY EVENT ====================

    socket.on('delivery:new-order', (data) => {
      console.log('🔔 Legacy event received:', data);
      fetchActiveOrders();
      toast.info('New order assigned!', {
        position: 'bottom-right',
        autoClose: 3000,
      });
    });

    // ✅ NEW: ORDER CANCELLED EVENT HANDLER
    socket.on('order:cancelled', (data) => {
      console.log('\n🚫 ═══════════════════════════════════════');
      console.log('ORDER CANCELLED - EVENT RECEIVED!');
      console.log('═══════════════════════════════════════');
      console.log('Full Data:', JSON.stringify(data, null, 2));
      console.log('Order ID:', data.orderId);
      console.log('Reason:', data.reason);
      console.log('Cancelled By:', data.cancelledBy);
      console.log('Timestamp:', data.timestamp);
      console.log('═══════════════════════════════════════\n');

      // Play cancellation sound (softer)
      playNotificationSound();

      // Remove from active orders and pending notifications
      setActiveOrders((prev) => prev.filter((order) => order._id !== data.orderId));
      setPendingNotifications((prev) => prev.filter((order) => order._id !== data.orderId));

      // ✅ ADD TO CANCELLED NOTIFICATIONS
      const cancelledOrder = {
        ...data.order,
        status: 'cancelled',
        cancellationReason: data.reason,
        cancelledBy: data.cancelledBy,
        cancelledAt: data.cancelledAt || new Date()
      };
      
      setCancelledNotifications((prev) => {
        // Remove if already exists
        const filtered = prev.filter((order) => order._id !== data.orderId);
        // Add new cancelled order at top, keep only 5
        return [cancelledOrder, ...filtered.slice(0, 4)];
      });

      // Update notification count (cancelled orders still count as notifications)
      setNotificationCount((prev) => Math.max(0, prev));

      // Browser notification
      if ('Notification' in window && Notification.permission === 'granted') {
        new Notification('Order Cancelled! ❌', {
          body: `Order #${data.orderId?.slice(-6)} - ${data.reason || 'Cancelled'}`,
          icon: '/logo192.png',
          badge: '/favicon.ico',
          requireInteraction: true
        });
      }

      // Toast notification
      toast.warning(`🚫 Order #${data.orderId?.slice(-6).toUpperCase()} Cancelled`, {
        position: 'top-right',
        autoClose: 5000
      });
    });

    // ==================== ORDER STATUS CHANGED EVENT (UPDATED) ====================

    socket.on('order:status-changed', (data) => {
      console.log('📦 Order status changed:', data);
      
      // Refresh orders to update the list with new statuses
      fetchActiveOrders();
      
      // Handle specific status changes
      if (data.newStatus === 'delivered') {
        // Remove from active orders
        setActiveOrders((prev) => prev.filter((order) => order._id !== data.orderId));
        setPendingNotifications((prev) => prev.filter((order) => order._id !== data.orderId));
      } else if (data.newStatus === 'cancelled') {
        // Handle cancellation through status change
        setActiveOrders((prev) => prev.filter((order) => order._id !== data.orderId));
        setPendingNotifications((prev) => prev.filter((order) => order._id !== data.orderId));
        
        const cancelledOrder = {
          ...data.order,
          status: 'cancelled',
          cancellationReason: data.reason || 'Status changed to cancelled',
          cancelledBy: data.updatedBy || 'System',
          cancelledAt: new Date()
        };
        
        setCancelledNotifications((prev) => {
          const filtered = prev.filter((order) => order._id !== data.orderId);
          return [cancelledOrder, ...filtered.slice(0, 4)];
        });
      }
      
      setNotificationCount((prev) => Math.max(0, prev));
    });

    socket.on('order:rejected', (data) => {
      console.log('📡 Order rejected:', data);
      setActiveOrders((prev) => prev.filter((order) => order._id !== data.orderId));
      setPendingNotifications((prev) => prev.filter((n) => n._id !== data.orderId));
      setNotificationCount((prev) => Math.max(0, prev - 1));
      
      toast.info('Order rejected', {
        position: 'bottom-right',
        autoClose: 2000
      });
    });

    // Log all events for debugging
    socket.onAny((eventName, ...args) => {
      console.log(`📨 Event received: ${eventName}`, args);
    });

    // ==================== CLEANUP ====================

    return () => {
      console.log('🧹 Cleaning up socket connection...');
      socket.disconnect();
      socketRef.current = null;
    };
  }, [user?._id, user?.fullName, playNotificationSound, fetchActiveOrders]);

  // ==================== UPDATE NOTIFICATION COUNT ON STATE CHANGE ====================
  useEffect(() => {
    const total = pendingNotifications.length + cancelledNotifications.length;
    setNotificationCount(total);
    console.log('📊 Notification count updated:', {
      pending: pendingNotifications.length,
      cancelled: cancelledNotifications.length,
      total
    });
  }, [pendingNotifications.length, cancelledNotifications.length]);

  // ==================== HANDLERS ====================

  // CORRECTED: Accept order now changes status to 'shipped' (not 'processing')
  const handleAcceptOrder = useCallback(async (orderId) => {
    let loadingToastId;
    try {
      loadingToastId = toast.loading('Accepting order...', {
        position: 'bottom-right',
      });

      console.log('✅ Accepting order:', orderId);

      const response = await axiosInstance.put(
        `/api/delivery-person/${user._id}/orders/${orderId}/accept-with-otp`,
        { timeout: 10000 }
      );

      if (response.data) {
        // Update local state to 'shipped'
        setActiveOrders((prevOrders) =>
          prevOrders.map((order) =>
            order._id === orderId ? { ...order, status: 'shipped' } : order
          )
        );

        // Update pending notifications with new status
        setPendingNotifications((prev) =>
          prev.map((order) =>
            order._id === orderId ? { ...order, status: 'shipped' } : order
          )
        );

        if (socketRef.current?.connected) {
          socketRef.current.emit('order:status-update', {
            orderId,
            status: 'shipped',
            deliveryPersonId: user._id,
            timestamp: new Date().toISOString()
          });
        }

        toast.dismiss(loadingToastId);
        toast.success('Order picked up! OTP sent to customer. 📦', {
          position: 'bottom-right',
          autoClose: 3000,
        });

        // Refresh orders to get updated data from server
        setTimeout(() => {
          fetchActiveOrders();
        }, 500);
      }
    } catch (error) {
      toast.dismiss(loadingToastId);
      console.error('❌ Error accepting order:', error);
      toast.error('Failed to accept order', {
        position: 'bottom-right',
        autoClose: 4000
      });
      throw error;
    }
  }, [axiosInstance, user._id, fetchActiveOrders]);

  const handleRejectOrder = useCallback(async (orderId) => {
    let loadingToastId;
    try {
      loadingToastId = toast.loading('Rejecting order...', {
        position: 'bottom-right',
      });

      const currentOrder = activeOrders.find((o) => o._id === orderId);
      if (!currentOrder) {
        toast.dismiss(loadingToastId);
        throw new Error('Order not found');
      }

      const response = await axiosInstance.post(
        `/api/orders/${orderId}/reject`,
        {
          rejectionReason: 'Rejected by delivery agent',
          deliveryPersonName: user?.fullName || 'Delivery Agent',
          deliveryPersonId: user?._id,
          rejectedAt: new Date().toISOString(),
        },
        { timeout: 10000 }
      );

      if (response.data?.success) {
        setActiveOrders((prev) => prev.filter((o) => o._id !== orderId));
        setPendingNotifications((prev) => prev.filter((n) => n._id !== orderId));
        setNotificationCount((prev) => Math.max(0, prev - 1));

        if (socketRef.current?.connected) {
          socketRef.current.emit('order:rejected', {
            orderId,
            deliveryPersonId: user?._id,
            timestamp: new Date().toISOString(),
          });
        }

        toast.dismiss(loadingToastId);
        toast.success('Order rejected!', {
          position: 'bottom-right',
          autoClose: 3000,
        });
      }
    } catch (error) {
      toast.dismiss(loadingToastId);
      console.error('❌ Error rejecting order:', error);
      toast.error('Failed to reject order');
      throw error;
    }
  }, [axiosInstance, user?._id, user?.fullName, activeOrders]);

  const toggleNotificationDropdown = useCallback(() => {
    setShowNotificationDropdown(prev => !prev);
  }, []);

  const closeNotificationDropdown = useCallback(() => {
    setShowNotificationDropdown(false);
  }, []);

  const navigateToHistory = useCallback(() => {
    setShowNotificationDropdown(false);
    navigate('/history');
  }, [navigate]);

  // ✅ RETURN CANCELLED NOTIFICATIONS
  return {
    notificationCount,
    showNotificationDropdown,
    pendingNotifications,
    cancelledNotifications, // ✅ EXPORTED
    activeOrders,
    socketConnected,
    handleAcceptOrder,
    handleRejectOrder,
    toggleNotificationDropdown,
    closeNotificationDropdown,
    navigateToHistory,
    fetchActiveOrders,
  };
};

